'use client';

import Script from 'next/script';

import type { ReactNode } from 'react';

import CheckRequireUserData from '@shared/common/components/CheckRequireUserData/CheckRequireUserData';
import Device from '@shared/common/components/Device';
import { Footer } from '@shared/common/components/Footer';
import { NavLayout } from '@shared/common/components/NavLayout';
import {
  launchpadCampaignsNavItem,
  launchpadMyAccountNavItem,
  launchpadPrimaryNav,
  launchpadTokenSalesNavItem,
} from '@shared/common/components/NavLayout/navSetting';
import type { NavLayoutLinkBase } from '@shared/common/components/NavLayout/types';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useBanxaModal, useSupportModal } from '@shared/common/providers/GlobalModalsProvider';
import { getProcessEnvGlobalConfig } from '@shared/config/global';
import { nextProjectUrl } from '@shared/constants/urls';

import { useAuthModal } from '@launchpad/components/ui/buttons/ConnectWalletButton';
import { Launchpad } from '@ui/uikit/components/logos/Launchpad';

const quickNavItems: NavLayoutLinkBase[] = [
  launchpadMyAccountNavItem,
  launchpadTokenSalesNavItem,
  launchpadCampaignsNavItem,
];

const MainLayout = ({ children }: { children: ReactNode }) => {
  const isDev = getProcessEnvGlobalConfig('isDev');
  const { user, logout, userEvmWallet } = useAuth();
  const modal = useBanxaModal();
  const supportModal = useSupportModal();
  const { login } = useAuthModal();

  const handleConnectClick = async () => {
    await login();
  };

  const handleBanxaBuyToken = () => {
    modal.open({
      walletAddress: userEvmWallet?.attributes.address,
    });
  };

  return (
    <>
      <NavLayout
        logo={Launchpad}
        user={user ? { data: user } : undefined}
        onLogin={handleConnectClick}
        onLogout={logout}
        primaryNavItems={launchpadPrimaryNav}
        quickNavItems={quickNavItems}
        primaryNavMenuTitle="Magic Launchpad"
        showKyc
        onBanxaBuyToken={handleBanxaBuyToken}
        onSupport={supportModal.open}
      >
        {children}

        <Footer
          disclaimer={
            <>
              <p className="text-caption-xs-d text-base-text-quaternary">
                Magic Launchpad, part of the Magic Square Ecosystem, is a technology platform and
                does not act as your broker, agent, or legal advisor. All decisions, transactions,
                and investments are your responsibility. Blockchain and crypto-assets carry
                significant risks, including potential loss of all funds. Nothing on this website
                should be considered advice or a recommendation to buy; users must conduct their own
                research. Magic Launchpad does not provide custody, wallet, or financial services
                and facilitates peer-to-peer interactions only.
              </p>
              <p className="text-caption-xs-d text-base-text-quaternary">
                Participation is prohibited, but not limited to, from the USA, UK, Canada, China,
                Hong Kong, Yemen, Bosnia, Afghanistan, Seychelles, Libya, and all OFAC-sanctioned
                countries (e.g., DR Congo, Iran, Myanmar, Sudan, Iraq, Ivory Coast, North Korea,
                Venezuela, Syria, Zimbabwe, Cuba, Liberia).
              </p>
            </>
          }
          landingPageLinkUrl={nextProjectUrl.magicsquare('/')}
        />
      </NavLayout>
      <CheckRequireUserData user={user} />

      <Script src="https://accounts.google.com/gsi/client" async defer strategy="lazyOnload" />

      <Device>
        {({ isMobile }) =>
          isMobile &&
          isDev && (
            <Script
              src="https://cdn.jsdelivr.net/npm/eruda"
              onLoad={() => {
                // Initialize Eruda (devtools for mobile)
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                eruda.init();
              }}
            />
          )
        }
      </Device>
    </>
  );
};

export default MainLayout;
